import { useCallback, useEffect, useRef, useState } from 'react';
import { useServices } from '../app/services';
import { useAuth } from './AuthProvider';
import { AuthLayout } from './LoginPage';
import { Button, ErrorNotice, Field, Loading } from '../components/ui';
import type { TotpEnrollment } from './authService';
import { friendlyError, type FriendlyError } from '../lib/errors';

/** "JBSWY3DPEHPK3PXP" → "JBSW Y3DP EHPK 3PXP": makkelijker overtypen. */
export function groupSecret(secret: string): string {
  return secret.replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim();
}

/** TOTP-fout: "vraag een nieuwe code aan" klopt hier niet, de app maakt elke 30 s een nieuwe. */
function totpError(e: unknown): FriendlyError {
  const f = friendlyError(e);
  return f.kind === 'otp_invalid'
    ? { ...f, hint: 'Gebruik de code die nú in je authenticator-app staat. Een code is 30 seconden geldig. Klopt de tijd op je telefoon?' }
    : f;
}

/**
 * TOTP enrolment (first time) or verification. Required (aal2) for platform_admin and club_admin.
 */
export function MfaPage() {
  const { auth, mode } = useServices();
  const { refresh, signOut } = useAuth();
  const [factorId, setFactorId] = useState<string | null>(null);
  const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState('');
  const [error, setError] = useState<FriendlyError | null>(null);
  const [startError, setStartError] = useState<FriendlyError | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const lastTried = useRef<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setStartError(null);
    (async () => {
      try {
        const existing = await auth.verifiedTotpFactor();
        if (cancelled) return;
        if (existing) setFactorId(existing);
        else {
          const e = await auth.enrollTotp();
          if (cancelled) return;
          setEnrollment(e);
          setFactorId(e.factorId);
        }
      } catch (e) {
        setStartError(friendlyError(e, 'Tweestapsverificatie kon niet starten.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth, attempt]);

  const verify = useCallback(
    async (value: string) => {
      if (!factorId || busy) return;
      lastTried.current = value;
      setBusy(true);
      setError(null);
      try {
        await auth.verifyTotp(factorId, value);
        await refresh();
      } catch (err) {
        setError(totpError(err));
        setCode('');
        lastTried.current = null;
      } finally {
        setBusy(false);
      }
    },
    [auth, factorId, refresh, busy],
  );

  const onCode = (raw: string) => {
    const v = raw.replace(/\D/g, '').slice(0, 6);
    setCode(v);
    if (v.length < 6) lastTried.current = null;
    // Automatisch bevestigen bij 6 cijfers (niet opnieuw met dezelfde, foute code).
    if (v.length === 6 && v !== lastTried.current) void verify(v);
  };

  const qrSrc = enrollment?.qrCode
    ? enrollment.qrCode.startsWith('data:')
      ? enrollment.qrCode
      : `data:image/svg+xml;utf8,${encodeURIComponent(enrollment.qrCode)}`
    : null;

  return (
    <AuthLayout>
      <h2>{enrollment ? 'Tweestapsverificatie instellen' : 'Bevestig dat jij het bent'}</h2>
      <p className="muted" style={{ marginBottom: 20 }}>
        {enrollment
          ? 'Eenmalig, duurt ongeveer twee minuten. Beheerders loggen in met een tweede stap, omdat je toegang tot gebouwen beheert.'
          : 'Open je authenticator-app en vul de 6 cijfers bij Antreja in.'}
      </p>
      {loading ? (
        <Loading label="Tweede stap voorbereiden…" />
      ) : startError ? (
        <div className="stack">
          <ErrorNotice error={startError} onRetry={() => setAttempt((n) => n + 1)} />
          <Button variant="ghost" onClick={() => void signOut()}>
            Uitloggen
          </Button>
        </div>
      ) : (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            if (code.length === 6) void verify(code);
          }}
        >
          {enrollment && (
            <ol className="mfa-steps">
              <li>
                <strong>Installeer een authenticator-app</strong> op je telefoon, bijvoorbeeld Microsoft Authenticator of Google Authenticator. Heb je er al
                een? Gebruik die.
              </li>
              <li>
                <strong>Scan de QR-code</strong> met die app (tik op + of "Account toevoegen").
                <div className="mfa-qr">
                  {qrSrc ? (
                    <img className="qr" src={qrSrc} alt="QR-code om Antreja toe te voegen aan je authenticator-app" width={176} height={176} />
                  ) : (
                    <div className="qr qr-empty" role="img" aria-label="Geen QR-code in de demo">
                      {mode === 'demo' ? 'In de demo is er geen QR-code. Gebruik de sleutel.' : 'Geen QR-code ontvangen. Gebruik de sleutel.'}
                    </div>
                  )}
                  <div className="mfa-key">
                    <span className="small muted">Scannen lukt niet? Kies in de app "Sleutel invoeren" en typ:</span>
                    <code className="secret" aria-label={`Sleutel: ${enrollment.secret.split('').join(' ')}`}>
                      {groupSecret(enrollment.secret)}
                    </code>
                    <span className="mfa-key-actions">
                      <Button
                        size="sm"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(enrollment.secret.replace(/\s/g, ''));
                            setCopied(true);
                            setTimeout(() => setCopied(false), 2500);
                          } catch {
                            /* selecteren kan altijd */
                          }
                        }}
                      >
                        {copied ? 'Gekopieerd' : 'Kopieer sleutel'}
                      </Button>
                      <span className="small muted">Accountnaam: Antreja. Type: op tijd gebaseerd.</span>
                    </span>
                  </div>
                </div>
              </li>
              <li>
                <strong>Vul de 6 cijfers in</strong> die de app nu toont. We controleren automatisch.
              </li>
            </ol>
          )}
          <Field label="Code uit je authenticator-app" hint={mode === 'demo' ? 'Demo: elke 6 cijfers werken, 000000 toont een foute code.' : 'Een code is 30 seconden geldig.'}>
            {(p) => (
              <input
                {...p}
                aria-invalid={error ? true : undefined}
                className="input otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={7}
                value={code}
                readOnly={busy}
                onChange={(e) => onCode(e.target.value)}
                autoFocus
              />
            )}
          </Field>
          {busy && (
            <p className="muted small" role="status">
              Code controleren…
            </p>
          )}
          {error && <ErrorNotice error={error} />}
          <Button variant="primary" type="submit" disabled={busy || code.length !== 6}>
            {busy ? 'Controleren…' : 'Bevestigen'}
          </Button>
          <div className="mfa-recovery">
            <strong>Telefoon kwijt of nieuwe telefoon?</strong>{' '}
            {enrollment
              ? 'Bewaar de sleutel hierboven op een veilige plek, bijvoorbeeld in je wachtwoordmanager. Daarmee zet je Antreja zelf op een nieuwe telefoon. Geen sleutel meer?'
              : 'Zonder je authenticator-app kun je niet inloggen.'}{' '}
            Vraag een Antreja-platformbeheerder om je tweestapsverificatie te resetten. Dat kan alleen na controle van je identiteit.
          </div>
          <Button variant="ghost" onClick={() => void signOut()}>
            Uitloggen
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
